import type { SetAttributesFunction } from '../types';

/**
 * Updates nested attributes using dot notation path
 *
 * @param attributes - Current block attributes
 * @param setAttributes - Function to update block attributes
 * @param path - Dot notation path (e.g., 'style.title.fontSize', 'settings.display.customerName')
 * @param value - Value to set at the path
 */
export const updateAttributes = < T extends Record< string, unknown > >(
	attributes: T,
	setAttributes: SetAttributesFunction< T >,
	path: string,
	value: unknown,
) => {
	const pathParts = path.split( '.' );

	// Handle single-level paths directly
	if ( pathParts.length === 1 ) {
		setAttributes( { [ pathParts[ 0 ] ]: value } as Partial< T > );
		return;
	}

	const rootKey = pathParts[ 0 ];
	const currentRoot =
		( attributes[ rootKey ] as Record< string, unknown > ) ?? {};
	const newRoot = { ...currentRoot };
	let target = newRoot;

	// Navigate through the path (skip the root key)
	for ( let i = 1; i < pathParts.length - 1; i++ ) {
		const part = pathParts[ i ];
		if ( ! target[ part ] || typeof target[ part ] !== 'object' ) {
			target[ part ] = {};
		}
		target = target[ part ] as Record< string, unknown >;
	}

	// Set the final value
	const finalKey = pathParts[ pathParts.length - 1 ];
	if (
		typeof value === 'object' &&
		value !== null &&
		! Array.isArray( value )
	) {
		target[ finalKey ] = {
			...( ( target[ finalKey ] as Record< string, unknown > ) || {} ),
			...value,
		};
	} else {
		target[ finalKey ] = value;
	}

	// Update the root key in attributes
	setAttributes( { [ rootKey ]: newRoot } as Partial< T > );
};
